import { ReactNode, useEffect } from 'react';
import { ErrorBoundary, FallbackRender } from '@sentry/nextjs';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { redirectSigninPage } from '@zep/common/routes';
import { logger } from '@zep/core-logger';
import { setApiNetworkLoggerWeb } from '@zep/next';
import { Error500PageView } from '@zep/web-components';
const GlobalErrorBoundary = ({
  children
}: Props) => {
  useUnhandledRejectionErrors(event => {
    console.log('%c [JL] event - event', 'font-size: 16px; color:  red;', event);
    logger.captureError(event.reason?.error, {
      tags: {
        source: 'unhandledRejection'
      }
    });
  });

  // @ts-ignore
  const fallback: FallbackRender = ({
    error,
    resetError
  }) => {
    if (isServerError(error)) {
      switch (error.response.status) {
        case 401:
        case 403:
          resetError();
          redirectSigninPage();
          return null;
      }
    }
    return <Error500PageView data-sentry-element="Error500PageView" data-sentry-component="fallback" data-sentry-source-file="GlobalErrorBoundary.tsx" />;
  };
  return <ErrorBoundary fallback={fallback} beforeCapture={scope => {
    scope.setTag('globalErrorBoundaryRendered', true);
  }} onError={e => {
    const isNetworkError = setApiNetworkLoggerWeb(e);
    if (isNetworkError) {
      logger.track('on_network_error');
      return;
    }
    console.error('Error Boundary:', e);
    // 대시보드 호환: error_captured(auto)와 별도로 user-facing boundary 도달을 표시
    logger.debug('enter_error_boundary', {
      errorName: e instanceof Error ? e.name : 'Unknown'
    });
    logger.captureError(e, {
      tags: {
        source: 'GlobalErrorBoundary'
      }
    });
  }} data-sentry-element="ErrorBoundary" data-sentry-component="GlobalErrorBoundary" data-sentry-source-file="GlobalErrorBoundary.tsx">
      {children}
    </ErrorBoundary>;
};
export default GlobalErrorBoundary;
type Props = {
  children: ReactNode;
};
export interface ServerError<T = any, D = any> extends AxiosError<T, D> {
  response: AxiosResponse<T, D>;
}
export function isServerError<T>(error: unknown): error is ServerError<T> {
  return error != null && axios.isAxiosError(error) && typeof (error as ServerError<T>).response?.status !== 'undefined';
}
export const useUnhandledRejectionErrors = (handler: Handler) => {
  useEffect(() => {
    window.addEventListener('unhandledrejection', handler);
    return () => {
      window.removeEventListener('unhandledrejection', handler);
    };
  }, [handler]);
};
type Handler = (event: PromiseRejectionEvent) => void;